import { surfaceShadows } from '../../surfaceStyles';
import { Badge, Box, Button } from '@mui/material';
import {
  NavigationProps,
  NavigationCount,
  inventoryActivityLabel,
} from './navigation';

export const MOBILE_NAVIGATION_MIN_HEIGHT = 64;

export type MobileNavigationProps = Pick<
  NavigationProps,
  'value' | 'onChange' | 'itemActivity'
> & {
  onOpenCollection?: () => void;
  collectionsOpen?: boolean;
  collectionName?: string;
  itemCount?: NavigationCount;
  locationCount?: NavigationCount;
};

const countFormatter = new Intl.NumberFormat('en');
const compactCountFormatter = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const navigationPillStyles = {
  maxWidth: '100%',
  boxSizing: 'border-box',
  px: 1,
  py: 0.25,
  borderRadius: 999,
  bgcolor: 'background.default',
  color: 'text.primary',
  border: 0,
  boxShadow: surfaceShadows.control,
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: 1.25,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

function countDescription(count: NavigationCount, noun: string) {
  const total = countFormatter.format(count.total);
  return `${count.result === count.total ? total : `${countFormatter.format(count.result)} of ${total}`}${count.incomplete ? ' loaded' : ''} ${noun}${count.total === 1 ? '' : 's'}`;
}

function CountPill({ count, noun }: { count: NavigationCount; noun: string }) {
  const format = (value: number) =>
    count.total >= 10000
      ? compactCountFormatter.format(value)
      : countFormatter.format(value);
  return (
    <Box
      component="span"
      className="navigation-pill"
      aria-hidden="true"
      title={countDescription(count, noun)}
      sx={navigationPillStyles}
    >
      {count.result === count.total
        ? format(count.total)
        : `${format(count.result)} / ${format(count.total)}`}
      {count.incomplete ? '+' : ''}
    </Box>
  );
}

export function MobileNavigation({
  value,
  onChange,
  itemActivity,
  itemCount,
  locationCount,
  onOpenCollection,
  collectionsOpen,
  collectionName,
}: MobileNavigationProps) {
  return (
    <Box
      component="nav"
      aria-label="Primary navigation"
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${onOpenCollection ? 3 : 2}, minmax(0, 1fr))`,
        '& > button': {
          minWidth: 0,
          maxWidth: '100%',
          width: 'fit-content',
          justifySelf: 'center',
          minHeight: MOBILE_NAVIGATION_MIN_HEIGHT,
          px: 3,
          py: 0.5,
          flexDirection: 'column',
          gap: 0.5,
          borderRadius: 3,
          '& > .navigation-label': { transform: 'translateY(-4px)' },
          '& > .navigation-badge-row': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            width: '100%',
            height: 32,
            borderRadius: 999,
          },
          color: 'text.secondary',
          textTransform: 'none',
          fontSize: '0.75rem',
          fontWeight: 500,
          lineHeight: 1.25,
          '&[aria-current="page"]': {
            color: 'primary.main',
            fontWeight: 600,
            bgcolor: 'action.selected',
            boxShadow: surfaceShadows.inset,
            '& .navigation-pill': { bgcolor: 'primary.main', color: '#fff' },
          },
        },
      }}
    >
      <Button
        aria-label={`${inventoryActivityLabel(itemActivity)}${itemCount ? `, ${countDescription(itemCount, 'item')}` : ''}`}
        aria-current={value === 'items' ? 'page' : undefined}
        onClick={() => onChange('items')}
      >
        <Box component="span" className="navigation-badge-row">
          {itemCount && <CountPill count={itemCount} noun="item" />}
          {Boolean(itemActivity?.count) && (
            <Badge
              color={itemActivity?.failed ? 'warning' : 'primary'}
              badgeContent={itemActivity?.count}
              max={99}
              sx={{ position: 'absolute', top: 4, right: 8 }}
            />
          )}
        </Box>
        <span className="navigation-label">Inventory</span>
      </Button>
      <Button
        aria-label={
          locationCount
            ? `Locations, ${countDescription(locationCount, 'location')}`
            : undefined
        }
        aria-current={value === 'locations' ? 'page' : undefined}
        onClick={() => onChange('locations')}
      >
        <Box component="span" className="navigation-badge-row">
          {locationCount && <CountPill count={locationCount} noun="location" />}
        </Box>
        <span className="navigation-label">Locations</span>
      </Button>
      {onOpenCollection && (
        <Button
          aria-label={`Choose collection: ${collectionName || 'Collections'}`}
          aria-haspopup="dialog"
          aria-expanded={Boolean(collectionsOpen)}
          onClick={onOpenCollection}
        >
          <Box
            component="span"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: 32,
            }}
          >
            {collectionName && (
              <Box
                component="bdi"
                dir="auto"
                title={collectionName}
                sx={navigationPillStyles}
              >
                {collectionName}
              </Box>
            )}
          </Box>
          <span className="navigation-label">Collections</span>
        </Button>
      )}
    </Box>
  );
}
